// app/javascript/components/ColumnsConstructor/ColumnsConstructor.jsx
import React, { useState, useEffect } from 'react';
import { 
  Settings, 
  Plus, 
  Layers, 
  User, 
  Globe, 
  RotateCcw, 
  AlertTriangle, 
  Info 
} from 'lucide-react';

// Импорты компонентов и утилит
import { ColumnsTable, ChartGroupsTable } from './ColumnsTable';
import { ColumnForm, ChartGroupForm } from './ColumnForm';
import {
  initColumnForm,
  initChartGroupForm as initChartGroupFormData,
  handleChartGroupMetricsChange,
  handleMetricColorChange
} from './columnsUtils';
import {
  fetchEditingContext,
  fetchColumns,
  fetchChartGroups,
  switchToPersonalSettings,
  resetToDefaults,
  saveColumn,
  saveChartGroup,
  deleteColumn,
  deleteChartGroup,
  toggleColumnVisibility,
  toggleColumnChart
} from './columnsAPI';

const ColumnsConstructor = ({ 
  columns: initialColumns, 
  column_types,
  available_fields,
  error_categories,
  existing_columns
}) => {
  // Основные состояния
  const [columns, setColumns] = useState(initialColumns || []);
  const [chartGroups, setChartGroups] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  
  // Состояния форм
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isChartGroupFormOpen, setIsChartGroupFormOpen] = useState(false);
  const [editingColumn, setEditingColumn] = useState(null);
  const [editingChartGroup, setEditingChartGroup] = useState(null);
  const [formData, setFormData] = useState({});
  const [chartGroupFormData, setChartGroupFormData] = useState({});
  
  // Состояния для операций
  const [isDeleting, setIsDeleting] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  
  // Состояния для персональных настроек
  const [userStatus, setUserStatus] = useState({
    is_personalized: false,
    is_admin: false,
    stats: {}
  });
  
  const [editingContext, setEditingContext] = useState({
    editing_mode: 'anonymous',
    can_edit_defaults: false,
    can_reset_to_defaults: false,
    recommendations: []
  });
  
  const [showPersonalizationAlert, setShowPersonalizationAlert] = useState(false);

  // Загрузка данных при инициализации
  useEffect(() => {
    loadInitialData();
  }, []);

  // Проверяем, нужно ли показать предупреждение о персонализации
  useEffect(() => {
    if (!userStatus.is_admin && !userStatus.is_personalized && (isFormOpen || isChartGroupFormOpen)) {
      setShowPersonalizationAlert(true);
    }
  }, [isFormOpen, isChartGroupFormOpen, userStatus.is_admin, userStatus.is_personalized]);

  // Загрузка всех данных
  const loadInitialData = async () => {
    setIsLoading(true);
    try {
      const [contextData, columnsData, chartGroupsData] = await Promise.all([
        fetchEditingContext(),
        fetchColumns(),
        fetchChartGroups()
      ]);

      if (contextData) {
        setEditingContext(contextData);
        setUserStatus({
          is_personalized: contextData.is_personalized,
          is_admin: contextData.is_admin,
          stats: contextData.stats
        });
      }

      if (columnsData) {
        setColumns(columnsData.columns);
        if (columnsData.userStatus) {
          setUserStatus(prev => ({ ...prev, ...columnsData.userStatus }));
        }
      }

      setChartGroups(chartGroupsData);
    } catch (error) {
      console.error('Error loading initial data:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // Обновление данных после изменений
  const refreshData = async () => {
    try {
      const [contextData, columnsData, chartGroupsData] = await Promise.all([
        fetchEditingContext(),
        fetchColumns(),
        fetchChartGroups()
      ]);

      if (contextData) setEditingContext(contextData);
      if (columnsData) {
        setColumns(columnsData.columns);
        if (columnsData.userStatus) {
          setUserStatus(prev => ({ ...prev, ...columnsData.userStatus }));
        }
      }
      setChartGroups(chartGroupsData);
    } catch (error) {
      console.error('Error refreshing data:', error);
    }
  };

  // Инициализация формы для колонок
  const initForm = (column = null) => {
    setEditingColumn(column);
    setFormData(initColumnForm(column));
    setIsFormOpen(true);
  };

  // Инициализация формы для chart groups
  const initChartGroupForm = (chartGroup = null) => {
    setEditingChartGroup(chartGroup);
    setChartGroupFormData(initChartGroupFormData(chartGroup));
    setIsChartGroupFormOpen(true);
  };

  // Обработка изменений в форме колонок
  const handleFormChange = (field, value) => {
    setFormData(prev => {
      const newData = { ...prev, [field]: value };
      
      if (field === 'column_type' && prev.column_type !== value) {
        newData.calculation_config = {};
      }
      
      return newData;
    });
  };

  // Обработка изменений в форме chart groups
  const handleChartGroupFormChange = (field, value) => {
    setChartGroupFormData(prev => ({ ...prev, [field]: value }));
  };

  // Обработка изменений конфигурации
  const handleConfigChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      calculation_config: {
        ...prev.calculation_config,
        [field]: value
      }
    }));
  };

  // Обработка изменений конфигурации для мультиселекта
  const handleMultiSelectChange = (field, selectedOptions) => {
    const values = selectedOptions ? selectedOptions.map(option => option.value) : [];
    handleConfigChange(field, values);
  };

  // Сохранение колонки
  const handleSave = async () => {
    if (isSaving) return;
    
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      const confirmed = confirm('This will create your personal copy of all settings. Your changes will only affect your view. Continue?');
      if (!confirmed) return;
      
      try {
        await switchToPersonalSettings();
        await refreshData();
      } catch (error) {
        alert('Failed to switch to personal settings. Please try again.');
        return;
      }
    }
    
    setIsSaving(true);
    
    try {
      await saveColumn(formData, editingColumn, userStatus);
      setIsFormOpen(false);
      await refreshData();
    } catch (error) {
      console.error('Error saving column:', error);
      alert('Error saving column. Please check the console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  // Сохранение chart group
  const handleChartGroupSave = async () => {
    if (isSaving) return;
    
    if (!userStatus.is_admin && !userStatus.is_personalized) {
      const confirmed = confirm('This will create your personal copy of all settings. Your changes will only affect your view. Continue?');
      if (!confirmed) return;
      
      try {
        await switchToPersonalSettings();
        await refreshData();
      } catch (error) {
        alert('Failed to switch to personal settings. Please try again.');
        return;
      }
    }
    
    setIsSaving(true);
    
    try {
      await saveChartGroup(chartGroupFormData, editingChartGroup, userStatus);
      setIsChartGroupFormOpen(false);
      await refreshData();
    } catch (error) {
      console.error('Error saving chart group:', error);
      alert('Error saving chart group. Please check the console for details.');
    } finally {
      setIsSaving(false);
    }
  };

  // Удаление колонки
  const handleDelete = async (column) => {
    if (isDeleting) return;
    
    // Определяем тип удаления для сообщения
    const isSystemDelete = userStatus.is_admin && !userStatus.is_personalized;
    const deleteMessage = isSystemDelete 
      ? `Are you sure you want to delete "${column.label}" from the system? This will affect all users.`
      : `Are you sure you want to remove "${column.label}" from your view? It will remain available to other users.`;
    
    if (!confirm(deleteMessage)) return;
    
    setIsDeleting(true);
    
    try {
      await deleteColumn(column, userStatus);
      await refreshData();
    } catch (error) {
      if (error.message === 'Operation cancelled by user') {
        // Пользователь отменил создание персональных настроек
        return;
      }
      console.error('Error deleting column:', error);
      alert('Error deleting column. Please check the console for details.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Удаление chart group
  const handleChartGroupDelete = async (chartGroup) => {
    if (isDeleting) return;
    
    // Определяем тип удаления для сообщения
    const isSystemDelete = userStatus.is_admin && !userStatus.is_personalized;
    const deleteMessage = isSystemDelete 
      ? `Are you sure you want to delete chart group "${chartGroup.group_name}" from the system? This will affect all users.`
      : `Are you sure you want to remove chart group "${chartGroup.group_name}" from your view? It will remain available to other users.`;
    
    if (!confirm(deleteMessage)) return;
    
    setIsDeleting(true);
    
    try {
      await deleteChartGroup(chartGroup, userStatus);
      await refreshData();
    } catch (error) {
      if (error.message === 'Operation cancelled by user') {
        // Пользователь отменил создание персональных настроек
        return;
      }
      console.error('Error deleting chart group:', error);
      alert('Error deleting chart group. Please check the console for details.');
    } finally {
      setIsDeleting(false);
    }
  };

  // Переключение видимости колонки
  const handleToggleVisibility = async (column) => {
    try {
      await toggleColumnVisibility(column, userStatus);
      await refreshData();
    } catch (error) {
      console.error('Error toggling column visibility:', error);
    }
  };

  // Переключение показа колонки на графике
  const handleToggleChart = async (column) => {
    try {
      await toggleColumnChart(column, userStatus);
      await refreshData();
    } catch (error) {
      console.error('Error toggling column chart:', error);
    }
  };

  // Сброс к дефолтным настройкам
  const handleResetToDefaults = async () => {
    if (!confirm('Are you sure you want to reset all your personal settings to defaults? This action cannot be undone.')) {
      return;
    }
    
    try {
      await resetToDefaults();
      await refreshData();
      alert('Settings reset to defaults successfully!');
    } catch (error) {
      console.error('Error resetting to defaults:', error);
      alert('Failed to reset settings. Please try again.');
    }
  };

  // Переключение на персональные настройки
  const handleSwitchToPersonal = async () => {
    try {
      await switchToPersonalSettings();
      setShowPersonalizationAlert(false);
      await refreshData();
    } catch (error) {
      console.error('Error switching to personal settings:', error);
      alert('Failed to switch to personal settings. Please try again.');
    }
  };

  // Компонент: Индикатор статуса настроек
  const SettingsStatusIndicator = () => {
    if (editingContext.editing_mode === 'anonymous') {
      return (
        <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-gray-500" />
            <div>
              <h3 className="text-sm font-medium text-gray-800">Viewing Default Settings</h3>
              <p className="text-xs text-gray-600">Sign in to customize your statistics view</p>
            </div>
          </div>
        </div>
      );
    }

    if (userStatus.is_admin) {
      return (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-center gap-3">
            <Settings className="h-5 w-5 text-blue-600" />
            <div>
              <h3 className="text-sm font-medium text-blue-800">✏️ Editing Default Settings (Admin)</h3>
              <p className="text-xs text-blue-600">Changes affect all users without personal configurations</p>
            </div>
          </div>
        </div>
      );
    }

    if (userStatus.is_personalized) {
      return (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-green-600" />
              <div>
                <h3 className="text-sm font-medium text-green-800">👤 Personal Settings</h3>
                <p className="text-xs text-green-600">Your customized view - changes only affect you</p>
                {userStatus.stats.personalized_at && (
                  <p className="text-xs text-green-500 mt-1">
                    Personalized: {new Date(userStatus.stats.personalized_at).toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
            <button
              onClick={handleResetToDefaults}
              disabled={isLoading}
              className="px-3 py-1 text-xs bg-green-100 text-green-700 rounded hover:bg-green-200 transition-colors flex items-center gap-1"
            >
              <RotateCcw className="h-3 w-3" />
              Reset to Defaults
            </button>
          </div>
        </div>
      );
    }

    return (
      <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Globe className="h-5 w-5 text-yellow-600" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">🌐 Using Default Settings</h3>
              <p className="text-xs text-yellow-600">Any change will create your personal copy of all settings</p>
            </div>
          </div>
          <button
            onClick={handleSwitchToPersonal}
            disabled={isLoading}
            className="px-3 py-1 text-xs bg-yellow-100 text-yellow-700 rounded hover:bg-yellow-200 transition-colors flex items-center gap-1"
          >
            <User className="h-3 w-3" />
            Personalize Settings
          </button>
        </div>
      </div>
    );
  };

  // Компонент: Предупреждение о персонализации
  const PersonalizationAlert = () => {
    if (!showPersonalizationAlert) return null;

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-6 max-w-md mx-4">
          <div className="flex items-center gap-3 mb-4">
            <AlertTriangle className="h-6 w-6 text-yellow-600" />
            <h3 className="text-lg font-medium text-gray-900">Create Personal Settings?</h3>
          </div>
          <p className="text-sm text-gray-600 mb-6">
            This action will create your personal copy of all settings. Your changes will only affect your view 
            and won't impact other users. You can reset to defaults at any time.
          </p>
          <div className="flex gap-3 justify-end">
            <button
              onClick={() => {
                setShowPersonalizationAlert(false);
                setIsFormOpen(false);
                setIsChartGroupFormOpen(false);
              }}
              className="px-4 py-2 text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              onClick={handleSwitchToPersonal}
              className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600"
            >
              Create Personal Settings
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="container mx-auto px-4 py-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center gap-2">
            <Settings className="h-8 w-8" />
            Statistics Columns Constructor
          </h1>
          <p className="text-gray-600 mt-1">Configure columns and chart groups for the statistics table and charts</p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={() => initChartGroupForm()}
            disabled={isSaving}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              isSaving
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-indigo-500 hover:bg-indigo-600 text-white'
            }`}
          >
            <Layers className="h-4 w-4" />
            Add New Chart Group
          </button>
          <button
            onClick={() => initForm()}
            disabled={isSaving}
            className={`px-4 py-2 rounded-lg font-medium transition-colors flex items-center gap-2 ${
              isSaving
                ? 'bg-gray-400 cursor-not-allowed text-white'
                : 'bg-blue-500 hover:bg-blue-600 text-white'
            }`}
          >
            <Plus className="h-4 w-4" />
            Add New Column
          </button>
        </div>
      </div>

      {/* Индикатор статуса настроек */}
      <SettingsStatusIndicator />

      {/* Recommendations */}
      {editingContext.recommendations && editingContext.recommendations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <div className="flex items-start gap-3">
            <Info className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-blue-800 mb-2">Recommendations</h3>
              <ul className="text-sm text-blue-700 space-y-1">
                {editingContext.recommendations.map((rec, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <span className="w-1 h-1 bg-blue-500 rounded-full mt-2 flex-shrink-0"></span>
                    <span>{rec}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Chart Groups Section */}
      <ChartGroupsTable
        chartGroups={chartGroups}
        onEdit={initChartGroupForm}
        onDelete={handleChartGroupDelete}
        isDeleting={isDeleting}
        isSaving={isSaving}
        userStatus={userStatus}
      />

      {/* Columns Section */}
      <ColumnsTable
        columns={columns}
        onEdit={initForm}
        onDelete={handleDelete}
        onToggleVisibility={handleToggleVisibility}
        onToggleChart={handleToggleChart}
        isDeleting={isDeleting}
        isSaving={isSaving}
        userStatus={userStatus}
      />

      {/* Column Form Modal */}
      <ColumnForm
        isOpen={isFormOpen}
        onClose={() => setIsFormOpen(false)}
        formData={formData}
        onFormChange={handleFormChange}
        onConfigChange={handleConfigChange}
        onMultiSelectChange={handleMultiSelectChange}
        onSave={handleSave}
        editingColumn={editingColumn}
        availableFields={available_fields}
        columns={columns}
        errorCategories={error_categories}
        columnTypes={column_types}
        isLoading={isLoading}
        isSaving={isSaving}
        userStatus={userStatus}
      />

      {/* Chart Group Form Modal */}
      <ChartGroupForm
        isOpen={isChartGroupFormOpen}
        onClose={() => setIsChartGroupFormOpen(false)}
        formData={chartGroupFormData}
        onFormChange={handleChartGroupFormChange}
        onMetricsChange={(selectedOptions) => 
          handleChartGroupMetricsChange(chartGroupFormData, setChartGroupFormData, selectedOptions)
        }
        onMetricColorChange={(metric, color) => 
          handleMetricColorChange(chartGroupFormData, setChartGroupFormData, metric, color)
        }
        onSave={handleChartGroupSave}
        editingChartGroup={editingChartGroup}
        availableFields={available_fields}
        columns={columns}
        isLoading={isLoading}
        isSaving={isSaving}
        userStatus={userStatus}
      />

      {/* Предупреждение о персонализации */}
      <PersonalizationAlert />
    </div>
  );
};

export default ColumnsConstructor;